/** Match {{ token }} placeholders; non-greedy inner capture. */
export const VARIABLE_TOKEN_PATTERN = /\{\{\s*([\s\S]*?)\s*\}\}/g;
const EXPRESSION_PATTERN = /\[\[([\s\S]*?)\]\]/g;

export const isMalformedVariableToken = (fullToken = '') =>
  String(fullToken).includes('{{') || String(fullToken).includes('}}');

const normalizeExpressionText = (text = '') =>
  String(text ?? '').replace(EXPRESSION_PATTERN, (_match, inner) => {
    const innerText = String(inner ?? '');
    const variableMatches = innerText.match(new RegExp(VARIABLE_TOKEN_PATTERN.source, 'g')) || [];
    return variableMatches.join('');
  });

/**
 * Split cell text into literal text and variable token segments.
 * "{{mailingCity}}, {{mailingState}}" →
 *   [{ type: 'text', value: '' }, { type: 'variable', fullToken: 'mailingCity' }, ...]
 */
export const parseCellVariableSegments = (text = '') => {
  const source = normalizeExpressionText(text);
  if (!source) return [];

  const segments = [];
  let lastIndex = 0;
  const regex = new RegExp(VARIABLE_TOKEN_PATTERN.source, 'g');
  let match;

  while ((match = regex.exec(source)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: source.slice(lastIndex, match.index) });
    }
    const fullToken = match[1].trim();
    segments.push({
      type: 'variable',
      fullToken,
      name: fullToken.split('|')[0].trim()
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < source.length) {
    segments.push({ type: 'text', value: source.slice(lastIndex) });
  }

  return segments;
};

export const getVariableSegmentsFromMeta = (variableMeta, cellText = '') => {
  if (Array.isArray(variableMeta?.segments) && variableMeta.segments.length) {
    return variableMeta.segments;
  }

  const source = variableMeta?.rawValue ?? cellText;
  return parseCellVariableSegments(source);
};

export const isMixedVariableCell = (variableMeta, cellText = '') => {
  const segments = getVariableSegmentsFromMeta(variableMeta, cellText);
  const variables = segments.filter(s => s.type === 'variable');
  if (variables.length === 0) return false;
  if (variables.length > 1) return true;
  return segments.some(s => s.type === 'text' && String(s.value).trim() !== '');
};

export const getPrimaryVariableToken = (variableMeta, cellText = '') => {
  const segments = getVariableSegmentsFromMeta(variableMeta, cellText);
  const firstVar = segments.find(s => s.type === 'variable');
  return firstVar?.fullToken || variableMeta?.fullToken || '';
};
